import React from 'react';
import type { FormikHelpers } from 'formik';
import { Formik } from 'formik';
import { isEmpty } from 'lodash';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import Heading from '~/components/core/TextComponents/Heading';
import Text from '~/components/core/TextComponents/Text';
import type { UploadedFileData } from '~/components/SystemConfiguration/Communications/PhoneConfigurationTab/VoiceMessageConfigurationDialog/types';
import VoiceMessageConfigurationDialog from '~/components/SystemConfiguration/Communications/PhoneConfigurationTab/VoiceMessageConfigurationDialog/VoiceMessageConfigurationDialog';
import VoiceMessageDetailsPreview from '~/components/SystemConfiguration/Communications/PhoneConfigurationTab/VoiceMessageDetailsPreview/VoiceMessageDetailsPreview';
import type {
  PhoneProfileInfo,
  PhoneProfileInfoPhoneProfileVoiceMessageRef as VoiceMessageConfiguration,
} from '~/server_shared/openapi/data-contracts';

const INBOUND_FORM_IDS = {
  TEXT_OR_RECORDED_MESSAGE: 'inbound_text_or_recorded_message',
  GREETING_TEXT: 'inbound_greeting_text',
  VOICE_FILE: 'inbound_voice_file',
  IS_UPLOADING_VOICE_FILE: 'is_uploading_inbound_voice_file',
} as const;

interface FormValues {
  [INBOUND_FORM_IDS.TEXT_OR_RECORDED_MESSAGE]: 'text_message' | 'uploaded_text_message';
  [INBOUND_FORM_IDS.GREETING_TEXT]: string;
  [INBOUND_FORM_IDS.VOICE_FILE]?: UploadedFileData;
  [INBOUND_FORM_IDS.IS_UPLOADING_VOICE_FILE]?: boolean;
}

interface EditInboundGreetingConfigDialogProps {
  configuration?: VoiceMessageConfiguration | null;
  onCancel: () => void;
  onSubmit: (params: { configuration_type: string; message_text?: string; stored_file_id?: number }) => Promise<void>;
}

const EditInboundGreetingConfigDialog: React.FC<EditInboundGreetingConfigDialogProps> = ({
  configuration,
  onCancel,
  onSubmit,
}) => {
  const [allowSavingWithoutNewFile, setAllowSavingWithoutNewFile] = React.useState(
    !!configuration?.permanent_audio_file_url
  );

  const handleSubmit = async (values: FormValues, formikProps: FormikHelpers<FormValues>) => {
    try {
      await onSubmit({
        configuration_type: values?.inbound_text_or_recorded_message,
        message_text: values?.inbound_greeting_text,
        stored_file_id: values?.inbound_voice_file?.stored_file_id,
      });
      onCancel();
    } catch {
      formikProps.setSubmitting(false);
    }
  };

  return (
    <Formik<FormValues>
      initialValues={
        isEmpty(configuration)
          ? {
              [INBOUND_FORM_IDS.TEXT_OR_RECORDED_MESSAGE]: 'text_message',
              [INBOUND_FORM_IDS.GREETING_TEXT]: '',
              [INBOUND_FORM_IDS.VOICE_FILE]: undefined,
            }
          : {
              [INBOUND_FORM_IDS.TEXT_OR_RECORDED_MESSAGE]: configuration?.configuration_type ?? 'text_message',
              [INBOUND_FORM_IDS.GREETING_TEXT]: configuration?.message_text || '',
              [INBOUND_FORM_IDS.VOICE_FILE]: undefined,
            }
      }
      validationSchema={Yup.object().shape({
        [INBOUND_FORM_IDS.TEXT_OR_RECORDED_MESSAGE]: Yup.string()
          .oneOf(['text_message', 'uploaded_text_message'])
          .required(),
        [INBOUND_FORM_IDS.GREETING_TEXT]: Yup.string().when([INBOUND_FORM_IDS.TEXT_OR_RECORDED_MESSAGE], {
          is: 'text_message',
          then: Yup.string().required('Please enter a message'),
        }),
        [INBOUND_FORM_IDS.VOICE_FILE]: Yup.mixed().when([INBOUND_FORM_IDS.TEXT_OR_RECORDED_MESSAGE], {
          is: 'uploaded_text_message',
          then: allowSavingWithoutNewFile ? Yup.mixed() : Yup.mixed().required('Please select a file'),
        }),
      })}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, handleSubmit: formikHandleSubmit }) => {
        return (
          <CardDialog
            title="Configure inbound phone greeting"
            isDialog
            maxWidth="sm"
            fullWidth
            onClose={onCancel}
            preventClose={isSubmitting}
            footerActions={
              <DialogFooterActions
                disabled={isSubmitting}
                onClickPrimary={(e) => {
                  e.preventDefault();
                  formikHandleSubmit();
                }}
                onClickSecondary={onCancel}
              />
            }
          >
            <VoiceMessageConfigurationDialog
              voiceMessageConfiguration={configuration}
              textAreaLabel="Inbound call greeting"
              radioButtonGroupId={INBOUND_FORM_IDS.TEXT_OR_RECORDED_MESSAGE}
              textAreaId={INBOUND_FORM_IDS.GREETING_TEXT}
              voiceFileInputId={INBOUND_FORM_IDS.VOICE_FILE}
              onReplaceExistingRecording={() => {
                setAllowSavingWithoutNewFile(false);
              }}
            />
          </CardDialog>
        );
      }}
    </Formik>
  );
};

interface InboundGreetingConfigViewProps {
  profile: PhoneProfileInfo;
}

const InboundGreetingConfigView: React.FC<InboundGreetingConfigViewProps> = ({ profile }) => {
  const configuration = profile.inbound_call_message_configuration;

  return (
    <>
      <Heading variant={Heading.TYPES.H3}>Configure inbound phone greeting</Heading>
      <Text colorVariant={Text.COLOR_VARIANTS.DISABLED} variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.SEMI_BOLD}>
        Upload a voice message or add a text message for the inbound phone greeting that the caller hears before the
        dial sound starts.
      </Text>
      {profile.inbound_call_message_enabled && !isEmpty(configuration) ? (
        <div className="mt-20">
          <VoiceMessageDetailsPreview
            configurationType={configuration?.configuration_type || ''}
            messageTextTitle="Greeting by text message"
            messageText={configuration?.message_text}
            uploadedTextMessageTitle="Greeting by recording"
            audioUrl={configuration?.permanent_audio_file_url}
          />
        </div>
      ) : null}
    </>
  );
};

export { EditInboundGreetingConfigDialog, InboundGreetingConfigView };
